import { useMemo, useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { computeCategoryAverages, shortLabel } from "./chart-utils";
import type { ChartLocation } from "./useChartData";

interface CategoryRadarChartProps {
  locations: ChartLocation[];
  profileWeights: Record<string, number> | null;
}

const SIZE = 300;
const CENTER = SIZE / 2;
const RADIUS = 120;
const GRID_LEVELS = [25, 50, 75, 100];

function polarToCartesian(value: number, index: number, count: number): { x: number; y: number } {
  const angleStep = (2 * Math.PI) / count;
  const startAngle = -Math.PI / 2;
  const angle = startAngle + index * angleStep;
  const r = (value / 100) * RADIUS;
  return {
    x: CENTER + r * Math.cos(angle),
    y: CENTER + r * Math.sin(angle),
  };
}

function polygonPoints(values: number[], count: number): string {
  return values
    .map((v, i) => {
      const { x, y } = polarToCartesian(v, i, count);
      return `${x},${y}`;
    })
    .join(" ");
}

export function CategoryRadarChart({ locations }: CategoryRadarChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const avgScores = useMemo(() => computeCategoryAverages(locations), [locations]);

  const categoryNames = useMemo(() => Object.keys(avgScores), [avgScores]);

  if (locations.length === 0 || categoryNames.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        Score locations to see category breakdown.
      </p>
    );
  }

  const count = categoryNames.length;
  const scoreValues = categoryNames.map((cat) => avgScores[cat] ?? 0);

  return (
    <TooltipProvider>
      <div className="flex justify-center">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="w-full max-w-[400px] min-w-[280px]"
          role="img"
          aria-label="Category score radar chart"
        >
          {/* Grid circles */}
          {GRID_LEVELS.map((level) => (
            <polygon
              key={level}
              points={polygonPoints(
                categoryNames.map(() => level),
                count,
              )}
              fill="none"
              stroke="currentColor"
              strokeOpacity={0.1}
              strokeWidth={1}
            />
          ))}

          {/* Axis lines */}
          {categoryNames.map((cat, i) => {
            const { x, y } = polarToCartesian(100, i, count);
            return (
              <line
                key={`axis-${cat}`}
                x1={CENTER}
                y1={CENTER}
                x2={x}
                y2={y}
                stroke="currentColor"
                strokeOpacity={0.1}
                strokeWidth={1}
              />
            );
          })}

          {/* Actual scores trace */}
          <polygon
            points={polygonPoints(scoreValues, count)}
            fill="var(--primary)"
            fillOpacity={0.15}
            stroke="var(--primary)"
            strokeWidth={2}
          />

          {/* Data points */}
          {scoreValues.map((v, i) => {
            const { x, y } = polarToCartesian(v, i, count);
            return (
              <circle
                key={`dot-${categoryNames[i]}`}
                cx={x}
                cy={y}
                r={hoveredIndex === i ? 5 : 3.5}
                fill="var(--primary)"
                className="transition-all"
              />
            );
          })}

          {/* Labels with tooltips */}
          {categoryNames.map((cat, i) => {
            const { x, y } = polarToCartesian(115, i, count);
            const score = avgScores[cat];
            return (
              <Tooltip key={`label-${cat}`}>
                <TooltipTrigger asChild>
                  <text
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="fill-muted-foreground text-[11px] cursor-default"
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {shortLabel(cat)}
                  </text>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-medium">{cat}</p>
                  <p className="text-xs text-muted-foreground">Avg Score: {score}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-6 mt-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-0.5 bg-primary rounded" />
          <span>Actual Scores</span>
        </div>
      </div>
    </TooltipProvider>
  );
}
